import RetentionReferenceInfo from '../TreatmentTrackerModal/RetentionReferenceInfo'
import React, { useCallback, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import { CompareSTLs, Download, LayerDisplayed, LayerHidden } from 'components/common/icons'
import { Grid } from '@material-ui/core'
import { useDispatch } from 'react-redux'
import DazzedParagraph12 from 'components/common/text/DazzedParagraph12'
import { useTranslation } from 'react-i18next'
import OriginSTLPicker from './OriginSTLPicker'
import AlignersCurrentStlTooth from '../../../../resources/AlignersCurrentStlTooth.png'
import BracesCurrentStlTooth from '../../../../resources/BracesCurrentStlTooth.png'
import { TreatmentTypes } from 'consts/treatmentConsts'
import LinkButton from 'components/common/buttons/LinkButton'
import Tooltip from 'components/common/Tooltip'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { isUserOfRole } from 'utils/authUtils'
import { Roles } from 'consts/authConsts'
import { trackEvent } from 'utils/analyticsUtils'
import { logError } from 'utils/logUtils'
import Actions from 'actions'
import { downloadStlsAsZip } from './downloadStls'
import { isStlModelVersionSupported } from './stlModelVersion'

const useStyles = makeStyles(theme => ({
  layerToggle: {
    cursor: 'pointer'
  },
  label: {
    marginBottom: 5,
    userSelect: 'none'
  },
  currentStlToothImage: {
    height: 21,
    width: 24
  },
  compareStlsLabel: {
    marginBottom: 5
  }
}))

const ViewerControls = ({
  viewerSettings,
  setViewerSettings,
  selectedOriginModelType,
  setSelectedOriginModelType,
  originModelOptions,
  scanNumber,
  alignerNumber,
  viewerScene,
  treatmentType,
  isCompareStlsModeOn,
  setIsCompareStlsModeOn,
  isRetentionResult = false,
  retentionReferenceId,
  grinScanId,
  currentActualStl,
  modelVersionId
}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { exportStl: exportStlFF } = useFeatureFlags()
  const isInternalSupportUser = useMemo(() => isUserOfRole([Roles.DevAdmin, Roles.OpsAdmin]), [])
  const canExportStl = exportStlFF || isInternalSupportUser
  const [isDownloading, setIsDownloading] = useState(false)

  const hasStls = Boolean(currentActualStl?.upper?.key && currentActualStl?.lower?.key)
  // Only meshes from the V1 model onwards may be handed to a practice, so a pre-V1 scan
  // hides the button entirely rather than showing it disabled. Applied to internal roles
  // too: the feature flag decides who sees the button at all, the model version decides
  // whether the mesh is fit to download, and that does not change with the viewer's role.
  const isModelVersionSupported = isStlModelVersionSupported(modelVersionId)
  // Redundant with the render condition below, and kept deliberately: this is a data-export
  // action, so the download itself must be impossible for an unsupported version even if
  // the visibility rule is later loosened.
  const canDownloadStls = hasStls && isModelVersionSupported

  const handleDownloadClick = useCallback(async () => {
    if (!canDownloadStls || isDownloading) return
    setIsDownloading(true)
    trackEvent('3D Tracker - Download STLs', {
      grinScanId,
      scanNumber,
      alignerNumber,
      treatmentType
    })
    try {
      await downloadStlsAsZip({
        upper: currentActualStl.upper,
        lower: currentActualStl.lower,
        scanNumber
      })
    } catch (error) {
      logError('Failed to download STLs', { grinScanId, error: error?.message })
      dispatch(
        Actions.showAlert({
          type: 'alert',
          title: t('general.genericOops'),
          message: t('dialogs.treatmentTracker.viewerControls.downloadSTLsFailed'),
          primaryButtonText: t('general.close')
        })
      )
    } finally {
      setIsDownloading(false)
    }
  }, [
    canDownloadStls,
    isDownloading,
    currentActualStl,
    grinScanId,
    scanNumber,
    alignerNumber,
    treatmentType,
    dispatch,
    t
  ])

  const controls = useMemo(
    () => [
      {
        isVisible: viewerSettings.isCurrentLayerVisible,
        setIsVisible: value =>
          setViewerSettings({
            ...viewerSettings,
            isCurrentLayerVisible: value
          }),
        icon: (
          <img
            className={classes.currentStlToothImage}
            src={
              treatmentType === TreatmentTypes.Aligners || isRetentionResult
                ? AlignersCurrentStlTooth
                : BracesCurrentStlTooth
            }
            alt=""
          />
        ),
        component: (
          <DazzedParagraph12 strong className={classes.label}>
            {t('dialogs.treatmentTracker.viewerControls.currentScan', {
              scanNumber,
              alignerNumber: `${
                !isRetentionResult && alignerNumber
                  ? t('dialogs.treatmentTracker.viewerControls.alignerNumber', { alignerNumber })
                  : ''
              }`
            })}
          </DazzedParagraph12>
        )
      },
      {
        isVisible: viewerSettings.isBaseLayerVisible,
        setIsVisible: value =>
          setViewerSettings({
            ...viewerSettings,
            isBaseLayerVisible: value
          }),
        component: (
          <Grid container alignItems="center" wrap="nowrap">
            <OriginSTLPicker
              selectedOriginModelType={selectedOriginModelType}
              setSelectedOriginModelType={setSelectedOriginModelType}
              originModelOptions={originModelOptions}
              viewerScene={viewerScene}
            />
            {isRetentionResult && <RetentionReferenceInfo referenceId={retentionReferenceId} />}
          </Grid>
        )
      }
    ],
    [
      alignerNumber,
      isRetentionResult,
      retentionReferenceId,
      classes,
      originModelOptions,
      viewerScene,
      scanNumber,
      selectedOriginModelType,
      setSelectedOriginModelType,
      setViewerSettings,
      t,
      treatmentType,
      viewerSettings
    ]
  )

  return (
    <Grid container alignItems="center" spacing={2}>
      {controls.map(({ isVisible, icon, setIsVisible, component }, index) => (
        <Grid item key={`viewer-control-${index}`}>
          <Grid container alignItems="center" spacing={1}>
            <Grid item className={classes.layerToggle} onClick={() => setIsVisible(!isVisible)}>
              {isVisible ? <LayerDisplayed /> : <LayerHidden />}
            </Grid>
            {icon && <Grid item>{icon}</Grid>}
            {component && <Grid item>{component}</Grid>}
          </Grid>
        </Grid>
      ))}
      {/* Compare mode selects across the whole timeline, which is an active-plan concept;
          a retention result only ever compares against its own reference. */}
      {!isCompareStlsModeOn && !isRetentionResult && (
        <Grid item>
          <LinkButton
            id="treatment-tracker-compare-stls-button"
            icon={<CompareSTLs />}
            label={t('dialogs.treatmentTracker.viewerControls.compareSTLs')}
            labelClassName={classes.compareStlsLabel}
            onClick={() => setIsCompareStlsModeOn(!isCompareStlsModeOn)}
          />
        </Grid>
      )}
      {canExportStl && isModelVersionSupported && !isCompareStlsModeOn && (
        <Grid item>
          <Tooltip
            value={hasStls ? '' : t('dialogs.treatmentTracker.viewerControls.downloadSTLsUnavailable')}
            placement="bottom"
          >
            <LinkButton
              icon={<Download />}
              label={t('dialogs.treatmentTracker.viewerControls.downloadSTLs')}
              labelClassName={classes.compareStlsLabel}
              disabled={!canDownloadStls || isDownloading}
              isLoading={isDownloading}
              keepTextWhileLoading
              onClick={handleDownloadClick}
            />
          </Tooltip>
        </Grid>
      )}
    </Grid>
  )
}

export default ViewerControls
